import { Fragment, type ReactNode } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { X } from "lucide-react";
import clsx from "clsx";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  /** Fixed action bar below the scrollable body (e.g. Speichern / Abbrechen). */
  footer?: ReactNode;
  className?: string;
  /** Extra classes for the scrollable body, e.g. a flex column that fills the panel. */
  bodyClassName?: string;
  size?: "md" | "lg" | "xl";
  /** Ohne Einblendung und ohne Unschärfe, wenn der Dialog sofort sichtbar sein soll. */
  instant?: boolean;
}

const sizeClasses = {
  md: "sm:max-w-2xl",
  lg: "sm:max-w-3xl",
  xl: "sm:max-w-5xl",
};

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  className,
  bodyClassName,
  size = "md",
  instant = false,
}: ModalProps) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter={instant ? "duration-0" : "ease-out duration-250"}
          enterFrom={instant ? "opacity-100" : "opacity-0"}
          enterTo="opacity-100"
          leave={instant ? "duration-0" : "ease-in duration-200"}
          leaveFrom="opacity-100"
          leaveTo={instant ? "opacity-0" : "opacity-0"}
        >
          <div
            className={clsx("fixed inset-0 bg-scrim", !instant && "backdrop-blur-[6px]")}
            aria-hidden="true"
          />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-0 sm:overflow-hidden sm:p-4 md:p-6">
          <div className="flex min-h-full items-end justify-center sm:h-full sm:min-h-0 sm:items-center">
            <Transition.Child
              as={Fragment}
              enter={instant ? "duration-0" : "ease-out duration-250"}
              enterFrom={instant ? "opacity-100" : "opacity-0 translate-y-4 sm:translate-y-2 sm:scale-[0.97]"}
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave={instant ? "duration-0" : "ease-in duration-200"}
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo={instant ? "opacity-0" : "opacity-0 translate-y-4 sm:translate-y-2 sm:scale-[0.97]"}
            >
              <Dialog.Panel
                className={clsx(
                  "eh-modal-panel w-full overflow-hidden border border-separator bg-card shadow-ios-lg",
                  "rounded-t-ios-lg sm:rounded-ios-lg",
                  "pb-[var(--eh-safe-bottom)] sm:pb-0",
                  sizeClasses[size],
                  className,
                )}
              >
                <div className="shrink-0 border-b border-separator bg-card-elevated/60 px-4 py-3.5 backdrop-blur-sm sm:px-5 sm:py-4">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <Dialog.Title className="text-base font-semibold tracking-tight text-label sm:text-lg">
                        {title}
                      </Dialog.Title>
                      {description ? (
                        <Dialog.Description className="mt-1 text-sm text-label-secondary">
                          {description}
                        </Dialog.Description>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="-mr-1 shrink-0 rounded-lg p-2 text-label-secondary transition-colors hover:bg-fill-tertiary hover:text-label focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                      onClick={onClose}
                      aria-label="Schließen"
                    >
                      <X className="h-5 w-5" aria-hidden />
                    </button>
                  </div>
                </div>
                <div
                  className={clsx(
                    "eh-modal-panel__body bg-card px-4 py-4 sm:px-5 sm:py-5",
                    bodyClassName,
                  )}
                >
                  {children}
                </div>
                {footer ? (
                  <div className="eh-modal-panel__footer eh-modal-actions shrink-0 border-t border-separator bg-card px-4 py-3 sm:px-5 sm:py-4">
                    {footer}
                  </div>
                ) : null}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
